import { CollapseToggle } from '../collapse';
import scrollTo from '../helpers/scrollTo';
import { initDropdown } from '../selects';
import { SELECTORS, SELECT_CONFIG, MAP_CONFIG, MODIFIERS } from './config';
import { GoogleMapsApi } from './map/GoogleMapsApi';
import { renderMarker } from './map/methods';
import { stylers } from './map/stylers';
import { getLocations, renderClearSearchButton, renderLocation } from './methods';

class LocationSearch {
    constructor(node) {
        this.node = node;
        this.defaultValue = this.node.dataset.defaultValue;
        this.apiUrl = this.node.dataset.apiUrl.replace('XXX', '');
        this.apiKey = this.node.dataset.apiKey;
        this.form = this.node.querySelector(SELECTORS.FORM);
        this.kindengezinPath = this.node.dataset.kindengezinPath;
        this.typeSelect = initDropdown({
            selectNode: this.form.querySelector(SELECTORS.TYPE_SELECT),
            config: SELECT_CONFIG,
        });
        this.locationInput = this.form.querySelector(SELECTORS.LOCATION_INPUT);
        this.resultHolder = this.node.querySelector(SELECTORS.RESULT_HOLDER);
        this.moreInfoHolder = this.node.querySelector(SELECTORS.MORE_INFO);
        this.mapHolder = this.node.querySelector(SELECTORS.MAP_HOLDER);
        this.value = this.defaultValue;
        this.inViewport = false;
        this.mapRendered = false;
        this.map = null;
        this.mapBounds = null;
        this.activeToggle = null;
        this.activeMarker = null;
        this.currentSearchBounds = null;
        this.activeLocation = null;
        this.markers = [];

        this.renderMap.bind(this);

        const observer = new IntersectionObserver(this.intersectionHandler.bind(this));
        observer.observe(node);
    }

    intersectionHandler(entries, self) {
        entries.forEach((entry) => {
            if (entry.isIntersecting && !this.inViewport) {
                this.inViewport = true;
                this.loadGoogleMaps();
                self.unobserve(entry.target);
            }
        });
    }

    loadGoogleMaps() {
        const GMAP_API = new GoogleMapsApi(this.apiKey);
        GMAP_API.load().then(() => {
            this.initLocationSearch();
        });
    }

    initLocationSearch() {
        this.updateLocations();
        this.handleSelect();
        this.handleSearch();
    }

    async updateLocations() {
        this.locations = await getLocations(this.apiUrl + this.value);
        if (!this.mapRendered) {
            this.renderMap();
        }

        this.renderMarkers();

        this.locations.forEach((location, i) => {
            const item = renderLocation(this.kindengezinPath, location);
            this.resultHolder.appendChild(item);
            this.locations[i].item = item;

            const toggle = item.querySelector(SELECTORS.ITEM_TOGGLE);
            const hasContactInfo = toggle.dataset.target !== undefined;
            let collapse = false;

            if (hasContactInfo) {
                collapse = new CollapseToggle(toggle);
            }

            toggle.addEventListener('click', (e) => {
                e.preventDefault();

                if (collapse) {
                    if (collapse.isOpen) {
                        collapse.hideContent();
                    } else {
                        collapse.showContent();
                    }
                }

                if (!toggle.classList.contains(MODIFIERS.TOGGLE_ACTIVE)) {
                    toggle.classList.add(MODIFIERS.TOGGLE_ACTIVE);

                    if (toggle !== this.activeToggle && this.activeToggle !== null) {
                        this.activeToggle.classList.remove(MODIFIERS.TOGGLE_ACTIVE);
                    }

                    if (this.activeLocation && this.activeLocation !== item) {
                        this.activeLocation.classList.remove(MODIFIERS.ITEM_ACTIVE);
                    }

                    if (this.activeLocation !== item) {
                        item.classList.add(MODIFIERS.ITEM_ACTIVE);
                        this.activeLocation = item;
                    }

                    if (this.activeMarker && this.activeMarker !== this.locations[i].marker) {
                        this.activeMarker.setIcon(stylers.icons.blue);
                    }

                    this.activeToggle = toggle;
                    this.activeMarker = this.locations[i].marker;
                    this.activeMarker.setIcon(stylers.icons.red);
                    this.map.setZoom(15);
                    this.map.panTo({
                        lat: parseFloat(toggle.dataset.latitude),
                        lng: parseFloat(toggle.dataset.longitude),
                    });
                } else {
                    toggle.classList.remove(MODIFIERS.TOGGLE_ACTIVE);
                    this.activeLocation.classList.remove(MODIFIERS.ITEM_ACTIVE);
                    this.activeMarker.setIcon(stylers.icons.blue);
                    this.activeToggle = null;
                    this.activeLocation = null;
                    this.activeMarker = null;
                    this.map.setZoom(MAP_CONFIG.zoom);
                    this.map.fitBounds(this.currentSearchBounds ? this.currentSearchBounds : this.mapBounds);
                }
            });
        });

        this.handleMarkerClick();
        this.mapHolder.parentNode.classList.remove(MODIFIERS.MAP_HOLDER_LOADING);
    }

    renderMap() {
        this.map = new google.maps.Map(this.mapHolder, MAP_CONFIG);
        this.mapBounds = new google.maps.LatLngBounds();
        this.mapRendered = true;
    }

    renderMarkers() {
        Object.entries(this.locations).forEach(([location, data]) => {
            const position = new google.maps.LatLng(data.ycoor, data.xcoor);
            this.locations[location].marker = renderMarker(this.map, position, data);
            this.mapBounds.extend(position);
        });

        this.map.fitBounds(this.mapBounds);
    }

    clearMarkers() {
        this.locations.forEach((location) => {
            location.marker.setMap(null);
        });
    }

    clearLocationHolder() {
        this.resultHolder.innerHTML = '';
    }

    clearSearch() {
        this.locationInput.value = '';
        this.map.fitBounds(this.mapBounds);
        this.removeClearSearchButton();
    }

    handleSelect() {
        this.typeSelect.passedElement.element.addEventListener('change', (e) => {
            const { value } = e.detail;
            this.mapHolder.parentNode.classList.add(MODIFIERS.MAP_HOLDER_LOADING);
            this.value = value;
            this.clearMarkers();
            this.clearLocationHolder();
            this.updateLocations();
            this.clearSearch();
        });
    }

    renderClearSearchButton() {
        const button = renderClearSearchButton(this.kindengezinPath, this.node.dataset.clearSearchText);
        button.addEventListener('click', (e) => {
            e.preventDefault();
            this.clearSearch();
        });
        this.clearSearchButton = button;
        this.moreInfoHolder.append(this.clearSearchButton);
    }

    removeClearSearchButton() {
        if (this.clearSearchButton) {
            this.clearSearchButton.parentNode.removeChild(this.clearSearchButton);
            this.clearSearchButton = null;
        }
    }

    handleSearch() {
        this.form.addEventListener('submit', (e) => {
            e.preventDefault();

            const formData = new FormData(this.form).entries();
            const searchParams = {};
            Array.from(formData).forEach((element) => {
                const [key, value] = element;
                searchParams[key] = value;
            });

            const geocoder = new google.maps.Geocoder();
            geocoder.geocode({
                address: searchParams['location-search-where'],
                region: 'BE',
            }, (results, status) => {
                if (status === 'OK') {
                    this.currentSearchBounds = results[0].geometry.bounds;
                    this.map.fitBounds(this.currentSearchBounds);
                    if (!this.clearSearchButton) {
                        this.renderClearSearchButton();
                    }
                } else {
                    this.map.fitBounds(this.mapBounds);
                    this.currentSearchBounds = null;

                    if (this.clearSearchButton) {
                        this.removeClearSearchButton();
                    }
                }
            });
        });
    }

    handleMarkerClick() {
        this.locations.forEach((location) => {
            const { marker } = location;

            google.maps.event.addListener(marker, 'click', () => {
                if (this.activeLocation) {
                    this.activeLocation.classList.remove(MODIFIERS.ITEM_ACTIVE);
                }

                if (this.activeMarker && this.activeMarker !== marker) {
                    this.activeMarker.setIcon(stylers.icons.blue);
                }

                location.item.classList.add(MODIFIERS.ITEM_ACTIVE);

                this.activeLocation = location.item;
                this.activeMarker = marker;
                this.activeMarker.setIcon(stylers.icons.red);

                scrollTo({
                    target: location.item,
                    offset: 50,
                    parent: this.resultHolder.parentNode,
                });
            });
        });
    }
}

export { LocationSearch };
